import * as React from "react";
import _ from "lodash";
import { useDebounce } from "hooks/useDebounce";
import { format } from "lib/globalize-util";
import { CircularProgress } from "material-ui";
import { List as VirtualizedList } from "react-virtualized/dist/commonjs/List";
import { InfiniteLoader } from "react-virtualized/dist/commonjs/InfiniteLoader";
import { ListItem } from "material-ui/List";
import TextField from "components/form/text-field";
import MuiTextField from "material-ui/TextField";
import Popover, { PopoverAnimationVertical } from "material-ui/Popover";
import { Search } from "@material-ui/icons";
import me from "models/me";
import { theme } from "styles/theme";
import { hasCompanyAccess } from "helpers/access-control";
import type { IScope } from "reducers/scope";
import type { TFDMCompanyRecord } from "records/company/company";
import { IUser } from "containers/management/management-types";
import { makeStyles } from "@material-ui/styles";
import { DATATEST } from "constants/integration";

const PAGE_LIMIT = 10;
const FIELD_WIDTH = {
    DEFAULT: 280,
    SEARCH_ICON_MODE: 340,
};
const ROW_HEIGHT = 40;
const LIST_HEIGHT = 300;

interface ICompanySelectProps {
    open: boolean;
    useSearchIcon?: boolean;
    disableLabel?: boolean;
    dataTest?: string;
    selectedCompany: TFDMCompanyRecord;
    onSelectedCompanyChange: (company: TFDMCompanyRecord) => void;
    errorText?: string;
    onErrorTextChange?: (errorText: string) => void;
    onFetchStartOrderCompanies: (params) => void;
    fetchingCompanies: boolean;
    companies: TFDMCompanyRecord[];
    companyIds: string[];
    hasMoreCompanies: boolean;
    user: IUser;
    scope: IScope;
}

export const CompanySelect = ({
    open,
    useSearchIcon = false,
    disableLabel = false,
    dataTest,
    selectedCompany,
    onSelectedCompanyChange,
    errorText,
    onErrorTextChange,
    onFetchStartOrderCompanies,
    fetchingCompanies,
    companies,
    companyIds,
    hasMoreCompanies,
    user,
    scope,
}: ICompanySelectProps) => {
    const [textFieldValue, setTextFieldValue] = React.useState<string>("");
    const [displayList, setDisplayList] = React.useState<boolean>(false);
    const textFieldRef = React.useRef<MuiTextField>(null);
    const debouncedTextfieldValue = useDebounce(textFieldValue, 500);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const isBuyer = React.useMemo<boolean>(() => me.isBuyer(), [me]);
    const useCompanyAccessControl = React.useMemo<boolean>(
        () => scope.brand?.hasPreference("company_access_control"),
        [scope?.brand],
    );

    const styles = React.useMemo(() => {
        const stylesObj = {
            clearStyle: {
                top: "30px",
                transform: undefined,
            },
            errorStyle: {
                position: "absolute",
                bottom: "-15px",
                marginTop: undefined,
            },
            hintStyle: {
                bottom: "0px",
                padding: "0 16px",
                lineHeight: "40px",
            },
            companyField: {
                paddingBottom: "5px",
            },
            companyFieldInput: {
                padding: "0px 32px 0px 16px",
                textOverflow: "ellipsis",
            },
            floatingLabel: {
                top: "21px",
                left: "4px",
            },
            floatingLabelShrink: {
                transform: "scale(1) translate(0px, 0px)",
                color: "gray",
            },
            loadingCompanies: {
                width: "100%",
                textAlign: "center",
                paddingBottom: "10px",
            },
            listItemStyle: {
                overflow: "hidden",
                whiteSpace: "nowrap" as any,
                textOverflow: "ellipsis",
            },
            listItemDisabledStyle: {
                color: theme.palette.grey,
            },
        };
        if (useSearchIcon) {
            stylesObj.clearStyle.top = "23px";
            stylesObj.companyFieldInput.padding = "0px 32px 0px 32px";
            stylesObj.hintStyle.padding = "0 34px";
            (stylesObj.hintStyle as any).top = "12px";
            (stylesObj.hintStyle as any).opacity = _.isEmpty(textFieldValue) ? 1 : 0;
            (stylesObj.loadingCompanies as any).paddingTop = "5px";
        }
        return stylesObj;
    }, [useSearchIcon, textFieldValue]);

    const handleTextFieldChange = React.useCallback(
        (e: React.SyntheticEvent<HTMLInputElement>, value: string) => {
            setTextFieldValue(value);
            onErrorTextChange?.(null);
        },
        [onErrorTextChange],
    );

    const handleDisplayList = React.useCallback(() => {
        setDisplayList(true);
    }, []);

    const handleHideList = React.useCallback(() => {
        setDisplayList(false);
    }, []);

    const handleSelectCompany = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent> | null) => {
            let company;
            if (e) {
                const dataId = (e.currentTarget as HTMLDivElement).getAttribute("data-id");
                company = companies[dataId];
            }
            onSelectedCompanyChange(company);
            if (company) {
                setTextFieldValue(company.__name);
            }
            setDisplayList(false);
            onErrorTextChange?.(null);
        },
        [companies, onErrorTextChange, onSelectedCompanyChange],
    );

    const isRowLoaded = React.useCallback(
        ({ index }) => {
            const companyId = companyIds[index];
            return companies[companyId];
        },
        [companies, companyIds],
    );

    const renderRow = React.useCallback(
        ({ index, style }) => {
            const companyId = companyIds[index];
            const companyListItem = companies[companyId];
            const disabled = isBuyer && useCompanyAccessControl && !hasCompanyAccess(companyListItem);
            return (
                <ListItem
                    key={companyListItem._id}
                    onClick={handleSelectCompany}
                    data-id={companyListItem._id}
                    style={style}
                    data-index={index}
                    data-test={DATATEST.GALLERY.COMPANY_SELECTION_OPTION}
                    innerDivStyle={{ ...styles.listItemStyle, ...(disabled ? styles.listItemDisabledStyle : {}) }}
                    disabled={disabled}
                >
                    {companyListItem.__name}
                </ListItem>
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [companyIds, companies, handleSelectCompany, isBuyer, useCompanyAccessControl],
    );

    const loadMoreRows = React.useCallback(() => {
        if (!fetchingCompanies && hasMoreCompanies) {
            const lastCompanyId = companyIds[companyIds.length - 1];
            const lastCompany = companies[lastCompanyId];
            const filters = {
                __search: debouncedTextfieldValue,
                __fields: "__all",
                __limit: PAGE_LIMIT,
                __last_id: lastCompany._id,
                __last_value: lastCompany.__name,
            };
            return new Promise(() => {
                onFetchStartOrderCompanies({
                    filters,
                    meta: {
                        replace: false,
                    },
                });
            });
        }
    }, [
        companies,
        debouncedTextfieldValue,
        hasMoreCompanies,
        fetchingCompanies,
        companyIds,
        onFetchStartOrderCompanies,
    ]);

    React.useEffect(() => {
        const companyName = open && selectedCompany ? selectedCompany.__name : "";
        setTextFieldValue(companyName);
    }, [open, selectedCompany]);

    React.useEffect(() => {
        if (selectedCompany && selectedCompany.__name !== textFieldValue) {
            handleSelectCompany(null);
        } else if (selectedCompany) {
            onSelectedCompanyChange(selectedCompany);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textFieldValue]);

    React.useEffect(() => {
        if (displayList) {
            const filters = {
                __search: debouncedTextfieldValue,
                __fields: "__all",
                __limit: PAGE_LIMIT,
            };
            onFetchStartOrderCompanies({
                filters,
                meta: {
                    replace: true,
                },
            });
        }
    }, [debouncedTextfieldValue, displayList, onFetchStartOrderCompanies]);

    const classes = useStyles({});

    return (
        <>
            <TextField
                label={!disableLabel && format("Company")}
                hintText={format("Select a company")}
                name="company"
                large={false}
                value={textFieldValue}
                onChange={handleTextFieldChange}
                fullWidth={true}
                hintStyle={styles.hintStyle}
                style={styles.companyField}
                inputStyle={styles.companyFieldInput}
                canClear={user?.account_type === "buyer" && _.size(companyIds) === 1 ? false : true}
                clearStyle={styles.clearStyle}
                onFocus={handleDisplayList}
                textFieldRef={textFieldRef}
                errorText={errorText}
                errorStyle={styles.errorStyle}
                floatingLabelText={useSearchIcon ? <Search className={classes.searchIcon} /> : null}
                floatingLabelStyle={styles.floatingLabel}
                floatingLabelShrinkStyle={styles.floatingLabelShrink}
                dataTest={dataTest}
            />
            <Popover
                open={displayList}
                anchorEl={textFieldRef.current && (textFieldRef.current as any).input}
                className="popover"
                onRequestClose={handleHideList}
                animation={PopoverAnimationVertical}
            >
                <InfiniteLoader
                    isRowLoaded={isRowLoaded}
                    loadMoreRows={loadMoreRows}
                    threshold={PAGE_LIMIT}
                    rowCount={1000}
                >
                    {({ onRowsRendered, registerChild }) => {
                        const listHeight =
                            companyIds.length * ROW_HEIGHT > LIST_HEIGHT ? LIST_HEIGHT : companyIds.length * ROW_HEIGHT;
                        return (
                            <VirtualizedList
                                ref={registerChild}
                                rowCount={(companyIds && companyIds.length) || 0}
                                rowRenderer={renderRow}
                                height={fetchingCompanies ? listHeight - 40 : listHeight}
                                width={useSearchIcon ? FIELD_WIDTH.SEARCH_ICON_MODE : FIELD_WIDTH.DEFAULT}
                                rowHeight={ROW_HEIGHT}
                                onRowsRendered={onRowsRendered}
                            />
                        );
                    }}
                </InfiniteLoader>
                {fetchingCompanies && (
                    <div style={styles.loadingCompanies as any}>
                        <CircularProgress size={30} thickness={3} />
                    </div>
                )}
            </Popover>
        </>
    );
};

const useStyles = makeStyles({
    searchIcon: {
        fontSize: "28px",
    },
});
